import {
  CANDIDATE_STATUS_LABELS_INTERNAL,
  LIVE_ROLE_STATUSES,
  type CandidateStatus,
} from '@remote-medium/shared';
import { cn } from '@remote-medium/ui';
import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { CandidateStatusChip, RoleStatusChip } from '../../../lib/status-chip.tsx';
import { CARD, H2, VIEW_ALL } from '../../../lib/ui.tsx';
import type { Overview } from './context.ts';

function CountCard({ title, to, children }: { title: string; to: string; children: ReactNode }) {
  return (
    <section className={cn(CARD, 'flex flex-col gap-4 p-5')}>
      <div className="flex items-center justify-between gap-4">
        <h2 className={H2}>{title}</h2>
        <Link to={to} className={VIEW_ALL}>
          View all
        </Link>
      </div>
      {children}
    </section>
  );
}

function CountRow({ chip, n, to }: { chip: ReactNode; n: number; to?: string }) {
  const inner = (
    <>
      {chip}
      <span className="text-sm font-medium text-foreground tabular-nums">{n}</span>
    </>
  );
  const cls = 'flex items-center justify-between border-t border-border py-2.5 no-underline';
  return (
    <li>
      {to ? (
        <Link to={to} className={cn(cls, 'hover:bg-muted/50')}>
          {inner}
        </Link>
      ) : (
        <div className={cls}>{inner}</div>
      )}
    </li>
  );
}

const CANDIDATE_STATUSES = Object.keys(CANDIDATE_STATUS_LABELS_INTERNAL) as CandidateStatus[];

export function CandidatePoolCard({ byStatus }: { byStatus: Overview['candidatesByStatus'] }) {
  const total = CANDIDATE_STATUSES.reduce((n, s) => n + (byStatus[s] ?? 0), 0);
  return (
    <CountCard title="Candidate pool" to="/candidates">
      <p className="m-0 flex items-baseline gap-2">
        <span className="text-3xl font-medium tabular-nums">{total}</span>
        <span className="text-sm text-muted-foreground">candidates in the pool</span>
      </p>
      <ul className="m-0 flex list-none flex-col p-0">
        {CANDIDATE_STATUSES.map((s) => (
          <CountRow
            key={s}
            to={`/candidates?status=${s}`}
            n={byStatus[s] ?? 0}
            chip={<CandidateStatusChip status={s} />}
          />
        ))}
      </ul>
    </CountCard>
  );
}

/** Live roles only: no drafts, nothing closed. */
export function RolesCard({ byStatus }: { byStatus: Overview['rolesByStatus'] }) {
  return (
    <CountCard title="Roles" to="/roles">
      <ul className="m-0 flex list-none flex-col p-0">
        {LIVE_ROLE_STATUSES.map((s) => (
          <CountRow key={s} n={byStatus[s] ?? 0} chip={<RoleStatusChip status={s} />} />
        ))}
      </ul>
    </CountCard>
  );
}
